import { flow } from 'fp-ts/function';
import { ListItem, Stack } from '@optimus-web/core';
import { LocalizedText } from '@mews/framework';
import { EnterprisePaths, enterpriseRoutePrefix } from '@mews-commander/ui';
import { LinkListItem, ListItemWithTooltip, SidebarFontIcon } from './style';
import {
    CreateListItemProps,
    CreateListItemWithLinkProps,
    CreatePrimaryTextProps,
    CreateRouteLinkProps,
} from './types';

const createPrimaryText = ({ textKey, text, label }: CreatePrimaryTextProps) => {
    const primaryText = text || (textKey && <LocalizedText textKey={textKey} />);
    return label ? (
        <Stack wrap spacing="200" itemAlignment="center">
            {primaryText}
            {label}
        </Stack>
    ) : (
        primaryText
    );
};

export const createListItem = ({
    text,
    textKey,
    iconName,
    tooltip,
    isMainItem,
    label,
    dataTestId,
    ...other
}: CreateListItemProps) => {
    const props = {
        ...other,
        tooltip,
        primaryText: createPrimaryText({ text, textKey, label }),
        leftIcon: iconName && <SidebarFontIcon value={iconName} />,
        dense: false,
        'data-test-id': dataTestId,
    };

    if (isMainItem) {
        return <ListItem {...props} />;
    }

    return tooltip ? <ListItemWithTooltip {...props} /> : <ListItem {...props} />;
};

const createRouteLink = <Path extends EnterprisePaths>({
    path,
    params,
    search,
    content,
}: CreateRouteLinkProps<Path>) => (
    <LinkListItem
        {...({ to: `${enterpriseRoutePrefix}${path}`, params, search } as React.ComponentProps<typeof LinkListItem>)}
    >
        {content}
    </LinkListItem>
);

export const createListItemWithLink = <Path extends EnterprisePaths>({
    text,
    textKey,
    iconName,
    tooltip,
    path,
    params,
    search,
    isMainItem,
    label,
    dataTestId,
}: CreateListItemWithLinkProps<Path>) =>
    flow(createListItem, (ListItemElement) =>
        createRouteLink({ path, params, search, content: ListItemElement } as CreateRouteLinkProps<Path>)
    )({
        text,
        textKey,
        iconName,
        tooltip,
        isMainItem,
        label,
        dataTestId,
    });
